import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye } from 'lucide-react'
import { useState } from 'react'
import type { ExamsApi } from '../api/exams'
import type { PracticeApi } from '../api/practice'
import { everyRow, type QuestionsApi } from '../api/questions'
import { QuestionDrawer } from '../questions/QuestionDrawer'
import { QuestionsGrid } from '../questions/QuestionsGrid'
import { useOpenInSimulator } from '../questions/openInSimulator'
import { PracticeUpload } from './PracticeUpload'

export function Practice({ api, questions, exams, exam }: { api: PracticeApi; questions: QuestionsApi; exams: ExamsApi; exam: string }) {
  const client = useQueryClient()
  const [paper, setPaper] = useState('')
  const [topic, setTopic] = useState('')
  const [selected, setSelected] = useState<number[]>([])
  const [opened, setOpened] = useState<number | null>(null)
  const query = useQuery({ queryKey: ['questions', exam], queryFn: () => everyRow(questions, { exam }) })
  const catalogue = useQuery({ queryKey: ['topics', exam], queryFn: () => exams.topics(exam) })
  const open = useOpenInSimulator((ids: number[]) => questions.preview(ids))
  const format = catalogue.data?.components.find((entry) => entry.componentCode === paper)
  const codes = new Set([
    ...(format?.sections.flatMap((session) => session.topics) ?? []),
    ...(catalogue.data?.topics.filter((entry) => entry.code.toUpperCase().startsWith(`${paper.toUpperCase()}-`)).map((entry) => entry.code) ?? []),
  ])
  const topics = (catalogue.data?.topics ?? []).filter((entry) => !paper || codes.has(entry.code))
  const rows = (query.data ?? []).filter((row) => row.kind !== 'Official' && !row.mockCode
    && (!paper || (row.topicCode !== null && codes.has(row.topicCode))) && (!topic || row.topicCode === topic))
  const selectedVisible = selected.filter((id) => rows.some((row) => row.id === id))
  const error = query.error?.message ?? catalogue.error?.message ?? open.error?.message

  return <>
    <div className="grid items-start gap-6 lg:grid-cols-2">
      <h2 className="font-heading text-h2 font-bold text-heading">Practice Management</h2>
      <PracticeUpload key={exam} api={api} exam={exam} onImported={() => {
        void client.invalidateQueries({ queryKey: ['questions'] })
        void client.invalidateQueries({ queryKey: ['question-topics'] })
      }} />
    </div>
    {error && <p role="alert" className="mt-4 border-l-2 border-red-700 pl-3 text-red-700">{error}</p>}
    <div className="mt-4 flex flex-wrap gap-3">
      <label className="font-bold text-heading">Paper
        <select className={input} value={paper} onChange={(event) => { setPaper(event.target.value); setTopic(''); setSelected([]) }}>
          <option value="">All Components</option>
          {catalogue.data?.components.map((entry) => <option key={entry.componentCode} value={entry.componentCode}>{entry.name}</option>)}
        </select>
      </label>
      <label className="font-bold text-heading">Topic Code
        <select className={input} value={topic} onChange={(event) => { setTopic(event.target.value); setSelected([]) }}>
          <option value="">All Topics</option>
          {topics.map((entry) => <option key={entry.code} value={entry.code}>{entry.code}: {entry.name}</option>)}
        </select>
      </label>
    </div>
    <QuestionsGrid rows={rows} pending={query.isPending} onSelectionChanged={setSelected} onOpen={setOpened} />
    {opened !== null && <QuestionDrawer api={questions} id={opened} onClose={() => setOpened(null)} onPreview={(id) => open.mutate([id])} />}
    {selectedVisible.length > 0 && <div role="status" className="fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 items-center gap-4 rounded-full border border-ink-100 bg-card px-5 py-3 shadow-lg">
      <span className="font-bold">{selectedVisible.length} selected</span>
      <button type="button" disabled={open.isPending} onClick={() => open.mutate(selectedVisible)} className="inline-flex items-center gap-2 font-bold text-green-800 disabled:opacity-55">
        <Eye aria-hidden size={16} /> {open.isPending ? 'Opening...' : 'View in Simulator'}
      </button>
    </div>}
  </>
}

const input = 'mt-1 block rounded border border-ink-100 px-3 py-2 font-normal'
